// src/elements/buttons.ts
// Factory functions for Storycore buttons — no HUD wiring here.

import { Z } from "../utils/constants";

export interface CCButton extends HTMLButtonElement {
  __setActive(on: boolean): void;
}
export interface NarrationMuteButton extends HTMLButtonElement {
  __update(muted: boolean): void;
}
export interface PauseOverlay extends HTMLElement {
  __setIcon(isPlaying: boolean): void;
  _button: HTMLButtonElement;
}

export type LikeButton = HTMLButtonElement & {
  __flash?: (kind: 'add' | 'remove' | number) => void;
  __setLiked?: (on: boolean) => void;
};

/** Icon state for the centered play/pause button.
 *  - boolean (legacy):  true => "pause", false => "play"
 *  - string:            "play" | "pause" | "replay"
 *    "replay" is used at the end of a non-looping reel so the user sees
 *    a circular-arrow icon instead of a play triangle (matches YouTube /
 *    Instagram convention for finished videos). */
export type PausePlayIconState = boolean | "play" | "pause" | "replay";

export type PausePlayButton = HTMLButtonElement & {
  __setIcon?: (state: PausePlayIconState) => void;
};

// ---- Base URL helper ----
function getBaseUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="storycore-base"]');
  return meta?.content?.replace(/\/$/, '') || '';
}
const baseUrl = getBaseUrl();

// ---- Shared Style Helper (additive) ----
function applyControlButtonStyle(button: HTMLButtonElement): void {
  button.type = "button";
  button.setAttribute("tabindex", "0");
  button.setAttribute("role", "button");
  button.classList.add("hud-btn","rounded-full","p-2","text-white","border-0","flex","items-center","justify-center");
}

// ---- Mute visuals only ----
export function updateMuteButtonUI(btn: HTMLButtonElement, isMuted: boolean): void {
  const icon = btn.querySelector<HTMLImageElement>(".mute-icon");
  if (!icon) return;
  btn.classList.toggle("is-muted", isMuted);
  btn.classList.toggle("is-unmuted", !isMuted);
  btn.setAttribute("aria-label", isMuted ? "Unmute video" : "Mute video");
  btn.title = isMuted ? "Unmute video" : "Mute video";
  btn.setAttribute("aria-pressed", String(isMuted));
  icon.src = `${baseUrl}/icons/${isMuted ? "mute.svg" : "unmute.svg"}`;
  icon.alt = isMuted ? "Muted" : "Unmuted";
}

// ---- Pure CC button (no wiring) ----
export function createCCButton(initialOn: boolean): CCButton {
  const btn = document.createElement("button");
  applyControlButtonStyle(btn);
  btn.classList.add("cc-button","hud-btn");

  // Track state locally but expose a setter for external sync
  let enabled = !!initialOn;

  // Preload icon paths
  const iconOn = `${baseUrl}/icons/cc-on.svg`;
  const iconOff = `${baseUrl}/icons/cc-off.svg`;

  // Build icon
  const icon = document.createElement("img");
  icon.className = "hud-icon";
  (icon.style as any).pointerEvents = "none";
  icon.style.objectFit = "contain";

  const updateVisual = (on: boolean) => {
    btn.classList.toggle("active", on);
    btn.setAttribute("aria-pressed", String(on));
    btn.setAttribute("aria-label", on ? "Hide captions" : "Show captions");
    btn.title = on ? "Hide captions" : "Show captions";
    icon.alt = on ? "Hide captions" : "Show captions";
    icon.src = on ? iconOn : iconOff;
  };

  updateVisual(enabled);
  btn.appendChild(icon);

  // Keep buttons passive; HUD layers will handle toggling + state changes
  btn.addEventListener("click", (e) => {
    // Prevent focus stealing / bubbling chaos; actual toggle handled upstream
    e.stopPropagation();
  });

  // External API: allow other code to force state/visual without re-creating the button
  (btn as any).__setActive = (on: boolean) => {
    enabled = !!on;
    updateVisual(enabled);
  };

  return btn as unknown as CCButton;
}

// ---- Pure Video Mute button (no wiring) ----
export function createVideoMuteButton(isMuted: boolean): HTMLButtonElement {
  const btn = document.createElement("button");
  applyControlButtonStyle(btn);
  btn.classList.add("mute-button","hud-btn");
  btn.setAttribute("aria-pressed", String(isMuted));
  const icon = document.createElement("img");
  icon.className = "mute-icon hud-icon";
  (icon.style as any).pointerEvents = "none";
  icon.alt = isMuted ? "Muted" : "Unmuted";
  btn.appendChild(icon);
  updateMuteButtonUI(btn, isMuted);
  return btn;
}

// ---- Pure Narration Mute button (no wiring) ----
export function createNarrationMuteButton(isMuted: boolean): NarrationMuteButton {
  const btn = document.createElement("button");
  applyControlButtonStyle(btn);
  btn.classList.add("narration-mute-button","narration-mute","hud-btn");
  btn.setAttribute("aria-pressed", String(isMuted));
  const icon = document.createElement("img");
  icon.className = "hud-icon";
  (icon.style as any).pointerEvents = "none";
  btn.appendChild(icon);
  const update = () => {
    btn.setAttribute("aria-label", isMuted ? "Unmute narration" : "Mute narration");
    btn.title = isMuted ? "Unmute narration" : "Mute narration";
    btn.setAttribute("aria-pressed", String(isMuted));
    icon.src = `${baseUrl}/icons/${isMuted ? 'narration-off.svg' : 'narration.svg'}`;
    icon.alt = isMuted ? "Muted narration" : "Unmuted narration";
  };
  update();
  (btn as any).__update = (muted: boolean) => { isMuted = muted; update(); };
  return btn as unknown as NarrationMuteButton;
}

export function createLikeButton(initialLiked = false): LikeButton {
  const btn = document.createElement("button");
  applyControlButtonStyle(btn);
  btn.classList.add("like-button", "hud-btn");
  btn.setAttribute("aria-pressed", String(!!initialLiked));
  if (initialLiked) btn.classList.add("liked");
  btn.setAttribute("aria-label", "Like this video");
  btn.title = "Like this video";

  // make sure we can layer a flash icon on top of the base icon
  if (getComputedStyle(btn).position === "static") {
    btn.style.position = "relative";
  }

  // Base icon that always shows the current "like" symbol
  const baseIcon = document.createElement("img");
  baseIcon.className = "hud-icon";
  baseIcon.src = `${baseUrl}/icons/like.svg`;
  baseIcon.alt = initialLiked ? "Liked" : "Like";
  (baseIcon.style as any).pointerEvents = "none";
  baseIcon.style.objectFit = "contain";
  btn.appendChild(baseIcon);

  // Expose a tiny UI helper to flash + / - without wiring server logic here
  // Usage: (btn as any).__flash('add') or __flash('remove') or __flash(+1/-1)
  (btn as any).__flash = (kind: "add" | "remove" | number) => {
    const add = typeof kind === "number" ? kind > 0 : kind === "add";
    const cls = add ? "anim-like-add" : "anim-like-remove";
    btn.classList.remove("anim-like-add", "anim-like-remove");
    void btn.offsetWidth;
    btn.classList.add(cls);
    clearTimeout((btn as any).__flashT);
    (btn as any).__flashT = setTimeout(() => {
      btn.classList.remove("anim-like-add", "anim-like-remove");
    }, 320);
  };

  // Allow HUD to force the liked visual state without recreating the button
  (btn as any).__setLiked = (on: boolean) => {
    btn.setAttribute("aria-pressed", String(!!on));
    btn.classList.toggle("liked", !!on);
    baseIcon.alt = on ? "Liked" : "Like";
    // If a filled heart icon is introduced later, swap here:
    // baseIcon.src = `${baseUrl}/icons/${on ? "like-fill.svg" : "like.svg"}`;
  };

  return btn as unknown as LikeButton;
}

// ---- Pure Pause/Play button (no overlay) ----
export function createPausePlayButton(isPaused: boolean): HTMLButtonElement {
  const btn = document.createElement("button");
  applyControlButtonStyle(btn);
  btn.classList.add("pause-button", "hud-btn");
  btn.style.setProperty("width", "calc(var(--hud-btn, 44px) * 1.25)", "important");
  btn.style.setProperty("height", "calc(var(--hud-btn, 44px) * 1.25)", "important");
  btn.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
  btn.setAttribute("aria-pressed", String(!isPaused));

  const icon = document.createElement("img");
  icon.className = "hud-icon";
  icon.alt = isPaused ? "Play" : "Pause";
  icon.src = `${baseUrl}/icons/${isPaused ? "play.svg" : "pause.svg"}`;
  (icon.style as any).pointerEvents = "none";
  btn.appendChild(icon);

  // Expose a state-based icon updater.
  // Accepts the legacy boolean OR a string state ("play" | "pause" | "replay").
  (btn as any).__setIcon = (state: PausePlayIconState) => {
    // Normalize boolean → string state (true = pause icon shown while playing)
    const resolved: "play" | "pause" | "replay" =
      typeof state === "boolean" ? (state ? "pause" : "play") : state;

    // restart-video.png lives next to play.svg / pause.svg in /icons/
    const fileMap: Record<typeof resolved, string> = {
      play:   "play.svg",
      pause:  "pause.svg",
      replay: "restart-video.png",
    };
    icon.src = `${baseUrl}/icons/${fileMap[resolved]}`;

    const labelMap: Record<typeof resolved, string> = {
      play:   "Play video",
      pause:  "Pause video",
      replay: "Replay video",
    };
    icon.alt = labelMap[resolved];
    btn.setAttribute("aria-label", labelMap[resolved]);
    // aria-pressed: only meaningful for the play/pause toggle.
    btn.setAttribute("aria-pressed", resolved === "pause" ? "true" : "false");

    // Class hook so CSS can style the replay state separately if needed
    btn.classList.toggle("is-ended", resolved === "replay");
  };

  return btn as unknown as PausePlayButton;
}

export function createVideoReelControls(): HTMLElement {
  const controls = document.createElement('div');
  controls.className = 'video-reel-controls reel-controls absolute top-2 right-2 flex flex-col items-end gap-2';
  controls.style.zIndex = String(Z.HUD);
  controls.style.transition = 'opacity 0.3s';
  return controls;
}
